import {
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { Eye, EyeHide } from '#icons';
import { checkNumberRange, handleMaxLength } from '../../../helpers/inputHelpers';

import { IconButton } from '../../IconButton';
import { colors } from '../../../../themes';
import {
  InnerLabelWrapper,
  InputLabel,
  OuterLabelWrapper,
  RequiredIndicator,
} from '../../../style';
import { Paragraph } from '../../../Typography';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../Data/Tooltip';
import { type InputType } from './types';
import {
  InputFieldWrapper,
  InputSuffix,
  PasswordWrapper,
  ShowPasswordWrapper,
  StyledInput,
  StyledInputWrapper,
  InputFieldDescription,
  InputFieldError,
  InputFieldContainer,
  TooltipWrapper,
} from './style';

type InputWrapperProps = InputFieldProps & {
  children?: ReactNode;
};

const InputWrapper = ({
  label,
  fullWidth,
  errorMessage,
  children,
  toolTipContent,
  required,
  showRequiredIndicator = true,
  description,
  ...rest
}: InputWrapperProps) => {
  const { name, mb } = rest;

  return (
    <InputFieldContainer fullWidth={fullWidth} mb={mb}>
      <InputFieldWrapper>
        <OuterLabelWrapper>
          {label && (
            <InnerLabelWrapper>
              <InputLabel htmlFor={name}>{label}</InputLabel>
              {!!required && showRequiredIndicator && <RequiredIndicator>*</RequiredIndicator>}
            </InnerLabelWrapper>
          )}
          {toolTipContent && (
            <TooltipWrapper>
              <Tooltip>
                <TooltipTrigger color={colors.primary.blue} />
                <TooltipContent>
                  <Paragraph variant="medium">{toolTipContent}</Paragraph>
                </TooltipContent>
              </Tooltip>
            </TooltipWrapper>
          )}
        </OuterLabelWrapper>
        {children}
      </InputFieldWrapper>

      {description && <InputFieldDescription>{description}</InputFieldDescription>}
      {!!errorMessage && <InputFieldError>{errorMessage}</InputFieldError>}
    </InputFieldContainer>
  );
};

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  type?: InputType;
  // Used by the PasswordInput component that
  // has a toggle to show/hide the password
  originalType?: InputType;
  value?: string | number;
  allowNegative?: boolean;
  placeholder?: string;
  max?: number;
  min?: number;
  disabled?: boolean;
  checkPassword?: boolean;
  required?: boolean;
  suffix?: string;
  errorMessage?: string;
  name?: string;
  autoComplete?: string;
  slim?: boolean;
  'data-testid'?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
};

const InputBase = ({ ...rest }: InputProps) => {
  const { name, maxLength, onChange, suffix } = rest;

  const dataTestId = rest['data-testid'];

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (maxLength) {
      e.target.value = handleMaxLength(e.target.value, maxLength);
    }

    onChange?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (maxLength) {
      e.target.value = handleMaxLength(e.target.value, maxLength);
    }

    rest.onBlur?.(e);
  };

  return (
    <StyledInputWrapper>
      {suffix && <InputSuffix disabled={rest.disabled}>{suffix}</InputSuffix>}
      <StyledInput
        {...rest}
        data-testid={dataTestId ?? 'text-field'}
        id={name}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </StyledInputWrapper>
  );
};

const PasswordInput = ({ ...rest }: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState<InputType>('password');

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
    setInputType(showPassword ? 'password' : 'text');
  };

  return (
    <PasswordWrapper>
      <InputBase {...rest} type={inputType} originalType="password" />
      <ShowPasswordWrapper>
        <IconButton variant="secondary" onClick={toggleShowPassword}>
          {showPassword ? <EyeHide /> : <Eye />}
        </IconButton>
      </ShowPasswordWrapper>
    </PasswordWrapper>
  );
};

const getParsableValue = (value: string | number) => {
  const parsableValue = value.toString().replace(/,/g, '.'); // Replace comma with period to be able to parse as a number

  return parsableValue;
};

const ensureSingleDecimal = (value: string) => {
  const normalized = value.replace(/,/g, '.');

  const parts = normalized.split('.');

  if (parts.length <= 1) return normalized;

  // Keep the first decimal separator and remove any additional ones

  const integerPart = parts[0];
  const decimalPart = parts.slice(1).join('');

  return integerPart + '.' + decimalPart.slice(0, 1); // Limit to one decimal places
};

const NumberInput = (inputProps: InputProps) => {
  const _props = { ...inputProps, min: inputProps.min ?? 0 };
  const { allowNegative = false, max, maxLength, min } = _props;
  const [currentValue, setCurrentValue] = useState(_props.value ?? '');

  const sanitizeNumberValue = (value: string) => {
    let sanitizedValue = value.replace(allowNegative ? /[^0-9.,-]/g : /[^0-9.,]/g, '');

    if (allowNegative) {
      sanitizedValue = sanitizedValue.replace(/(?!^)-/g, '');
    }

    return sanitizedValue;
  };

  const isIntermediateNumberValue = (value: string) => {
    const parsableValue = getParsableValue(value);

    return (
      parsableValue === '' ||
      parsableValue === '.' ||
      parsableValue === '-' ||
      parsableValue === '-.' ||
      parsableValue.endsWith('.')
    );
  };

  useEffect(() => {
    if (_props.value === undefined) {
      setCurrentValue('');
    } else {
      setCurrentValue(_props.value);
    }
  }, [_props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let nextValue = sanitizeNumberValue(e.target.value);

    if (maxLength) {
      nextValue = handleMaxLength(nextValue, maxLength);
    }

    nextValue = sanitizeNumberValue(nextValue);
    nextValue = ensureSingleDecimal(nextValue);

    if (isIntermediateNumberValue(nextValue)) {
      setCurrentValue(nextValue);
      _props.onChange?.(e);

      return;
    }

    const parsableValue = getParsableValue(nextValue);

    if (isNaN(Number(parsableValue))) {
      return;
    }

    const { numberTooHigh } = checkNumberRange({ value: parsableValue, max, min });
    const numberToSet = numberTooHigh ? max : nextValue;

    e.target.value = `${numberToSet}`;
    setCurrentValue(`${numberToSet}`);

    _props.onChange?.(e);
  };

  useEffect(() => {
    const parsableValue = getParsableValue(currentValue);

    const { numberToUse, numberIsInRange } = checkNumberRange({
      value: parsableValue,
      max,
      min,
    });

    if (!numberIsInRange) {
      setCurrentValue(numberToUse);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on MIN/MAX change.
  }, [min, max]);

  return (
    <InputBase
      {..._props}
      type="number"
      value={currentValue}
      onWheel={(e) => {
        e.currentTarget.blur();
      }}
      onKeyDown={(e) => {
        if (e.key === 'e' || e.key === 'E' || e.key === '+' || (!allowNegative && e.key === '-')) {
          e.preventDefault();
        }
      }}
      onBlur={(e) => {
        const rawValue = e.target.value;

        if (
          rawValue === '' ||
          rawValue === '.' ||
          rawValue === ',' ||
          rawValue === '-' ||
          rawValue === '-.' ||
          rawValue === '-,'
        ) {
          e.target.value = '';
          setCurrentValue('');
          _props.onBlur?.(e);

          return;
        }

        const sanitizedValue = sanitizeNumberValue(rawValue);
        const parsableValue = getParsableValue(sanitizedValue);

        if (isNaN(Number(parsableValue))) {
          e.target.value = '';
          setCurrentValue('');
          _props.onBlur?.(e);

          return;
        }

        const { numberToUse } = checkNumberRange({ value: parsableValue, max, min });

        e.target.value = numberToUse;
        setCurrentValue(numberToUse);
        _props.onBlur?.(e);
      }}
      onChange={(e) => {
        handleChange(e);
      }}
    />
  );
};

const EmailInput = ({ ...rest }: InputProps) => {
  return <InputBase {...rest} type="email" />;
};

export type InputFieldProps = InputProps & {
  label?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  toolTipContent?: ReactNode;
  mb?: string;
  description?: string;
  showRequiredIndicator?: boolean;
};

export const InputField = ({ label, fullWidth, ...rest }: InputFieldProps) => {
  let { type } = rest;

  if (!type) {
    type = 'text';
  }

  const getInputMarkup = (type: InputType) => {
    switch (type) {
      case 'password':
        return <PasswordInput {...rest} />;
      case 'number':
        return <NumberInput {...rest} />;
      case 'email':
        return <EmailInput {...rest} />;
      case 'tel':
        return <InputBase {...rest} type={type} />;
      default:
        return <InputBase {...rest} />;
    }
  };

  return (
    <InputWrapper {...rest} label={label} fullWidth={fullWidth}>
      {getInputMarkup(type)}
    </InputWrapper>
  );
};
